import React, { FC, ReactNode, useState, useEffect } from "react";
import clsx from "clsx";
import { VpdScrollable } from "../vpd-scrollable";
import { WorkspaceHeader } from "./workspace-header";
import styles from "./matter-relation-editor.css";

const {
  ["container"]: containerClassName,
  ["library"]: libraryClassName,
  ["library-closed"]: libraryClosedClassName,
  ["workspace"]: workspaceClassName,
  ["workspace-scrollable"]: workspaceScrollableClassName,
  ["workspace-content"]: workspaceContentClassName,
} = styles as any;

export type MatterRelationEditorProps<T extends boolean> = {
  template?: boolean;
  workspaceContent: ReactNode;
  addNew: T extends true ? () => void : undefined;
  showAddNew: T;
  reset?: T extends true ? () => void : undefined;
  showReset?: boolean;
  addNewLabel?: string;
  onCollapseAll?: () => void;
  onExpandAll?: () => void;
  showCollapseExpand?: boolean;
  somePhases?: boolean;
  handleAutoGenerate?: (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null
  ) => void;
  customModalVisible?: boolean;
  setCustomModalVisible?: (visible: boolean) => void;
  customPrompt?: string;
  setCustomPrompt?: (prompt: string) => void;
  libraryRender?: (open: boolean, setOpen: (open: boolean) => void) => ReactNode;
  showHeader?: boolean;
};

export const MatterRelationEditor = <T extends boolean>({
  template,
  libraryRender,
  workspaceContent,
  addNew,
  showAddNew,
  reset,
  showReset,
  addNewLabel,
  onCollapseAll,
  onExpandAll,
  showCollapseExpand,
  somePhases,
  handleAutoGenerate,
  customModalVisible,
  setCustomModalVisible,
  customPrompt,
  setCustomPrompt,
  showHeader = true,
}: MatterRelationEditorProps<T>): ReturnType<FC<MatterRelationEditorProps<T>>> => {
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [doScroll, setDoScroll] = useState<boolean>(false);

  useEffect(() => {
    if (doScroll && ref) {
      setDoScroll(false);
      setTimeout(() => {
        ref.scrollTop = ref.scrollHeight;
      }, 100);
    }
  }, [doScroll, ref]);

  const addNewFunc =
    showAddNew && addNew
      ? async () => {
          await addNew();
          setDoScroll(true);
        }
      : undefined;

  return (
    <div className={containerClassName}>
      {!template && libraryRender && (
        <div className={clsx(libraryClassName, libraryOpen || libraryClosedClassName)}>
          {libraryRender(libraryOpen, setLibraryOpen)}
        </div>
      )}
      <div className={workspaceClassName}>
        {showHeader && (
          <header>
            <WorkspaceHeader
              libraryOpen={libraryOpen}
              setLibraryOpen={!template ? setLibraryOpen : undefined}
              addNew={addNewFunc as any}
              showAddNew={showAddNew}
              showReset={showReset ?? false}
              reset={reset as any}
              labelOverride={addNewLabel}
              showCollapseExpand={showCollapseExpand}
              onCollapseAll={onCollapseAll}
              onExpandAll={onExpandAll}
              somePhases={somePhases}
              handleAutoGenerate={handleAutoGenerate}
              customPrompt={customPrompt}
              setCustomPrompt={setCustomPrompt}
              customModalVisible={customModalVisible}
              setCustomModalVisible={setCustomModalVisible}
            />
          </header>
        )}
        <VpdScrollable ref={(e: HTMLElement) => setRef(e)} className={workspaceScrollableClassName}>
          <main className={workspaceContentClassName}>{workspaceContent}</main>
        </VpdScrollable>
      </div>
    </div>
  );
};
